import React, { useEffect, useState } from "react";
import { X, Heart, CheckCircle, XCircle } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import Banner from "./Banner";

const ProductModal = ({
  product,
  isOpen,
  onClose,
  buildMode = false,
  onBuildFlowerSelect,
  onBuildAccessorySelect,
  onBuildVipSelect
}) => {
  const {
    selectedFlowers,
    selectedAccessories,
    selectedVips,
    addAccessory,
    removeAccessory,
    addFlower,
    removeFlower,
    addVip,
    removeVip,
  } = useCart();

  const { isInWishlist, addToWishlist, removeFromWishlist } = useWishlist();

  //const [hasBypassedPopup, setHasBypassedPopup] = useState(false);
  const [banner, setBanner] = useState({ show: false, type: "", message: "" });

  const showBanner = (type, message) => {
    setBanner({ show: true, type, message });
    setTimeout(() => {
      setBanner({ show: false, type: "", message: "" });
    }, 3000);
  };

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add("no-scroll");
    }
    return () => {
      setTimeout(() => {
        const stillOpen = document.querySelectorAll(".modal-open").length > 0;
        if (!stillOpen) {
          document.body.classList.remove("no-scroll");
        }
      }, 10);
    };
  }, [isOpen]);

  if (typeof isOpen !== "undefined" && (!isOpen || !product)) return null;
  if (!product) return null;

  const inStock = product.inStock !== false;
  const isSelected =
  product.category === "flower"
    ? selectedFlowers.some((f) => f._id === product._id)
    : product.category === "accessory"
    ? selectedAccessories.some((a) => a._id === product._id)
    : product.category === "vip"
    ? selectedVips.some((v) => v._id === product._id)
    : false;

  const handleAddClick = (e) => {
    e.stopPropagation();
    if (!inStock) return;

    // ---------------- BUILD MODE ----------------
    if (buildMode) {
      if (product.category === "flower" && onBuildFlowerSelect) {
        onBuildFlowerSelect(product);
      } else if (product.category === "accessory" && onBuildAccessorySelect) {
        onBuildAccessorySelect(product);
      } else if (product.category === "vip" && onBuildVipSelect) {
        onBuildVipSelect(product);
      }
      return; // build mode handles everything inside the modal
    }

    // ---------------- NORMAL SHOPPING ----------------
    if (product.category === "flower") {
      const flowerSelected = selectedFlowers.some(f => f._id === product._id);
      flowerSelected ? removeFlower(product._id) : addFlower(product);
      return;
    }

    if (product.category === "accessory") {
      const accessorySelected = selectedAccessories.some(a => a._id === product._id);
      accessorySelected ? removeAccessory(product._id) : addAccessory(product);
      return;
    }

    if (product.category === "vip") {
      const vipSelected = selectedVips.some(v => v._id === product._id);
      vipSelected ? removeVip(product._id) : addVip(product);
      return;
    }
  };


  const handleOverlayClick = (e) => {
    if (e.target._id === "product-modal-overlay") {
      onClose();
    }
  };

  const handleWishlistToggle = (e) => {
    e.stopPropagation();
    const wasWishlisted = isInWishlist(product._id);
    if (wasWishlisted) {
      removeFromWishlist(product._id);
      showBanner("info", `"${product.name}" removed from wishlist`);
    } else {
      addToWishlist(product);
      showBanner("success", `"${product.name}" added to wishlist`);
    }
  };

  return (
    <div
      id="product-modal-overlay"
      onClick={handleOverlayClick}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm px-4"
    >
      {banner.show && <Banner type={banner.type} message={banner.message} />}

      <div className="relative bg-white w-full max-w-5xl rounded-2xl shadow-2xl max-h-[90vh] overflow-hidden">

        {/* Close Button (Top Right) */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-red-500 z-30"
          aria-label="Close product modal"
        >
          <X size={28} />
        </button>

        {/* Wishlist Button (Top Left) */}
        <button
          onClick={handleWishlistToggle}
          aria-label="Toggle Wishlist"
          className={`absolute top-4 left-4 z-30 p-2 rounded-full bg-white shadow ${
            isInWishlist(product._id)
              ? "text-red-600"
              : "text-gray-600 hover:text-red-600"
          }`}
        >
          <Heart
            size={24}
            fill={isInWishlist(product._id) ? "currentColor" : "none"}
          />
        </button>

        {/* Scrollable Content */}
        <div className="flex flex-col md:flex-row gap-8 p-6 md:p-10 overflow-y-auto max-h-[90vh] pb-28 md:pb-10">

          {/* Product Image */}
          <div className="flex-shrink-0 w-full md:w-1/2">
            <img
              src={product.imageHighRes || product.image}
              alt={product.name}
              className="w-full h-64 md:h-96 object-cover rounded-xl"
            />
          </div>

          {/* Product Details */}
          <div className="flex-1 space-y-4">
            <h2 className="text-2xl md:text-3xl font-bold leading-snug">
              {product.name}
            </h2>

            <p className="text-gray-700">
              {product.descriptionLong || product.description}
            </p>

            <div className="mt-4 space-y-2">
              <div className="text-2xl font-bold text-blue-700">
                ${Number(product.price).toFixed(2)}
              </div>

              <div className="flex items-center gap-2 text-sm">
                {inStock ? (
                  <>
                    <CheckCircle className="text-green-500" size={18} />
                    <span className="text-green-600">In Stock</span>
                  </>
                ) : (
                  <>
                    <XCircle className="text-red-500" size={18} />
                    <span className="text-red-600">Out of Stock</span>
                  </>
                )}
              </div>

              <div className="text-sm mt-1 text-gray-500 space-y-1">
                {product.gender && <div>For: {product.gender}</div>}
                {product.category === "vip" && (
                  <div>
                    <span className="inline-block bg-yellow-300 text-yellow-900 px-2 py-0.5 rounded-full text-xs">
                      VIP
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Sticky Add to Cart (Mobile-safe) */}
        <div className="sticky bottom-0 z-20 bg-white border-t shadow-md px-6 py-4 md:static md:border-none md:shadow-none">
          <button
            disabled={!inStock}
            onClick={handleAddClick}
            className={`w-full py-3 text-white text-lg font-semibold rounded-xl transition ${
              !inStock
                ? "bg-gray-400 cursor-not-allowed"
                : isSelected
                ? "bg-green-600 hover:bg-green-700"
                : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            {!inStock ? (
              "Out of Stock"
            ) : isSelected ? (
              <>
                <CheckCircle className="inline mr-1" size={18} /> Added
              </>
            ) : (
              "Add to Cart"
            )}
          </button>
        </div>

      </div>
    </div>
  );
};

export default ProductModal;
